import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { Plus, Loader2, X, Layers, Image, Trash2, Edit } from 'lucide-react';

const MEDIA_TYPES = [
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
];

const emptyForm = { title: '', description: '', media_url: '', thumbnail_url: '', media_type: 'image', external_url: '', category: '', tags: '' };

export default function PortfolioManager() {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchPortfolio(); }, []);

  const fetchPortfolio = async () => {
    try {
      const res = await fetch(`${API}/portfolio/me`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setItems(d.items || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        media_url: form.media_url.trim() || null,
        thumbnail_url: form.thumbnail_url.trim() || form.media_url.trim() || null,
        media_type: form.media_type,
        external_url: form.external_url.trim() || null,
        category: form.category.trim() || null,
        tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      const url = editingId ? `${API}/portfolio/${editingId}` : `${API}/portfolio`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Failed to save');
      toast.success(editingId ? 'Item updated!' : 'Item added!');
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchPortfolio();
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title || '',
      description: item.description || '',
      media_url: item.media_url || '',
      thumbnail_url: item.thumbnail_url || '',
      media_type: item.media_type || 'image',
      external_url: item.external_url || '',
      category: item.category || '',
      tags: (item.tags || []).join(', '),
    });
    setEditingId(item.item_id);
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this portfolio item?')) return;
    try {
      const res = await fetch(`${API}/portfolio/${itemId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Item deleted');
      fetchPortfolio();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="portfolio-manager">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Layers className="w-5 h-5" />Your Portfolio</h3>
        <Button size="sm" onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }} data-testid="add-portfolio-btn">
          <Plus className="w-4 h-4 mr-1" />Add Item
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="glass-card border-border p-5 mb-5" data-testid="portfolio-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium">{editingId ? 'Edit Item' : 'New Portfolio Item'}</h4>
            <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); setEditingId(null); }}><X className="w-4 h-4" /></Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input placeholder="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="portfolio-title-input" />
            <Textarea placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="portfolio-desc-input" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Image/Video URL" value={form.media_url} onChange={e => setForm(f => ({ ...f, media_url: e.target.value }))} data-testid="portfolio-media-input" />
              <Input placeholder="Thumbnail URL (optional)" value={form.thumbnail_url} onChange={e => setForm(f => ({ ...f, thumbnail_url: e.target.value }))} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Select value={form.media_type} onValueChange={v => setForm(f => ({ ...f, media_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MEDIA_TYPES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="portfolio-category-input" />
              <Input placeholder="External link (optional)" value={form.external_url} onChange={e => setForm(f => ({ ...f, external_url: e.target.value }))} />
            </div>
            <Input placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
            <Button type="submit" disabled={submitting} className="w-full" data-testid="save-portfolio-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingId ? 'Update Item' : 'Add to Portfolio'}
            </Button>
          </form>
        </Card>
      )}

      {/* Grid */}
      {items.length === 0 ? (
        <Card className="glass-card border-border p-8 text-center">
          <Image className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No portfolio items. Showcase your work!</p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map(item => (
            <div key={item.item_id} className="group relative aspect-square rounded-xl overflow-hidden bg-muted" data-testid={`portfolio-manage-${item.item_id}`}>
              {(item.thumbnail_url || item.media_url) ? (
                <img src={item.thumbnail_url || item.media_url} alt={item.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Image className="w-10 h-10 text-muted-foreground/80" /></div>
              )}
              <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                <p className="text-foreground text-sm font-medium text-center px-2 line-clamp-2">{item.title}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => handleEdit(item)} data-testid={`edit-portfolio-${item.item_id}`}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(item.item_id)} data-testid={`delete-portfolio-${item.item_id}`}><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
